import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";

import { cleanSpecialCharacters } from "../../../extras";
import { dateTimeMask } from "../../../common/dateMask";
// APP COMPONENTS
import CardNumeric from "../../report/cardNumericInfo";
import LabelHorizontal from "../../labels/LabelHorizontal";
import ChannelReportSummary from "../../report/ChannelReportSummary";
import LabelToValueHorizontal from "../../labels/LabelToValueHorizontal";
import ValueToLabelHorizontal from "../../labels/ValueToLabelHorizontal";
import ChannelPOSReportSummary from "../../report/ChannelPOSReportSummary";
// MATERIAL UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DividerBlackOnPrint } from "../../styled";
import IconButton from "@mui/material/IconButton";
// ICONS
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { grey } from "@mui/material/colors";
import { HistoryReportGroupStackPrint } from "../../styled/stack";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import { formatFinance } from "../../../utils/formatters";
import Paper from "@mui/material/Paper";
import { alpha, useTheme } from "@mui/material";

export default function HistoryReportSummary({
  filters,
  reportData,
  reportLoading,
}) {
  const theme = useTheme();

  const componentRef = useRef(null);

  // Use the hook at the top level
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    onPrintError: (errorLocation, error) => {
      console.error(`Print error at ${errorLocation}:`, error);
      dispatchSnackBarMessage(
        `print-error-${Date.now()}`,
        `Erro ao imprimir: ${error.message}`,
        "error"
      );
    },
  });

  if (!reportData || reportLoading) {
    return <Typography></Typography>;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      {!reportLoading && (
        <>
          <Box ref={componentRef}>
            <CardNumeric
              label={`De ${dateTimeMask(filters.dateStart)} Até ${dateTimeMask(
                filters.dateEnd
              )}`}
              value=""
              icon="date"
            />

            <DividerBlackOnPrint sx={{ my: 1 }} />

            {filters.operator.value !== "ALL" && (
              <ValueToLabelHorizontal
                label={filters.operator.label}
                value="Operador"
              />
            )}
            <DividerBlackOnPrint sx={{ my: 1 }} />
            <ValueToLabelHorizontal
              value={filters.merchant === "ALL" ? "Lojas" : "Loja"}
              label={
                filters.merchant === "ALL" ? "Todas" : filters.merchant.label
              }
            />
            <DividerBlackOnPrint sx={{ my: 1 }} />

            <Box>
              <Typography variant="subtitle2">
                Os dados abaixos não incluem os valores relacionados a pedidos
                cancelados
              </Typography>
            </Box>

            <DividerBlackOnPrint sx={{ my: 1 }} />

            <HistoryReportGroupStackPrint spacing={1}>
              <LabelHorizontal label="Pedidos" align="center" isbold />
              <DividerBlackOnPrint />
              <LabelToValueHorizontal
                label="Delivery"
                value={reportData?.totals.delivery_orders}
              />
              <LabelToValueHorizontal
                label="Retirada"
                value={reportData?.totals.takeout_orders}
              />
              <LabelToValueHorizontal
                label="Salão"
                value={reportData?.totals.indoor_orders}
              />
              <LabelToValueHorizontal
                label="Total"
                value={reportData?.totals.orders}
                isbold
              />
            </HistoryReportGroupStackPrint>

            <HistoryReportGroupStackPrint spacing={1} sx={{ mt: 2 }}>
              <LabelHorizontal label="Receita" align="center" isbold />
              <DividerBlackOnPrint />
              <LabelToValueHorizontal
                label="Online"
                value={formatFinance(reportData?.totals.online_finance)}
                justifyContent="space-between"
              />
              <LabelToValueHorizontal
                label="Dinheiro"
                value={formatFinance(reportData?.totals.money_finance)}
                justifyContent="space-between"
              />
              <LabelToValueHorizontal
                label="Maquininha"
                value={formatFinance(reportData?.totals.card_finance)}
                justifyContent="space-between"
              />
              {reportData?.totals.vale_worker_amount > 0 && (
                <LabelToValueHorizontal
                  label="Vale Funcionário"
                  value={reportData?.totals.vale_worker_finance}
                  amount={reportData?.totals.vale_worker_amount}
                />
              )}

              {reportData?.totals.vale_refeicao_amount > 0 && (
                <LabelToValueHorizontal
                  label="Vale Refeição"
                  value={reportData?.totals.vale_refeicao_finance}
                  amount={reportData?.totals.vale_refeicao_amount}
                />
              )}

              {reportData?.totals.vale_outsider_amount > 0 && (
                <LabelToValueHorizontal
                  label="Vale Externo"
                  value={reportData?.totals.vale_outsider_finance}
                  amount={reportData?.totals.vale_outsider_amount}
                />
              )}

              {reportData?.totals.vale_fuel_amount > 0 && (
                <LabelToValueHorizontal
                  label="Vale Combustível"
                  value={reportData?.totals.vale_fuel_finance}
                  amount={reportData?.totals.vale_fuel_amount}
                />
              )}

              {reportData?.totals.vale_alimentacao_amount > 0 && (
                <LabelToValueHorizontal
                  label="Vale Alimentação"
                  value={reportData?.totals.vale_alimentacao_finance}
                  amount={reportData?.totals.vale_alimentacao_amount}
                />
              )}

              {reportData?.totals.pos_custom_payment_methods.map(
                (method, index) => (
                  <LabelToValueHorizontal
                    key={index}
                    label={cleanSpecialCharacters(method.title)}
                    value={formatFinance(method.total_value)}
                    justifyContent="space-between"
                  />
                )
              )}
              <LabelToValueHorizontal
                label="Total"
                value={formatFinance(reportData?.totals.finance)}
                isbold
                justifyContent="space-between"
              />
            </HistoryReportGroupStackPrint>

            <ChannelReportSummary channel="iFood" data={reportData?.ifood} />
            <ChannelReportSummary channel="Neemo" data={reportData?.neemo} />
            <ChannelReportSummary
              channel="Delivery Direto"
              data={reportData?.ddireto}
            />

            <ChannelPOSReportSummary data={reportData?.slapiepos} />
            <ChannelPOSReportSummary
              label={"Mesas"}
              data={reportData?.indoor}
            />

            <HistoryReportGroupStackPrint spacing={1} sx={{ mt: 2 }}>
              <LabelHorizontal label="Taxa de Entrega" align="center" isbold />
              <DividerBlackOnPrint />
              <LabelToValueHorizontal
                label="Total"
                value={formatFinance(reportData?.totals.delivery_tax)}
              />
            </HistoryReportGroupStackPrint>

            <HistoryReportGroupStackPrint spacing={1} sx={{ mt: 2 }}>
              <LabelHorizontal label="Cancelados" align="center" isbold />
              <DividerBlackOnPrint />
              <ValueToLabelHorizontal
                label={`Pedidos avaliados em ${formatFinance(
                  reportData?.totals.cancelled_orders_finance
                )}`}
                value={reportData?.totals.cancelled_orders}
              />
            </HistoryReportGroupStackPrint>
          </Box>
          <Box
            title="Imprimir"
            sx={{ display: "flex", p: 2, justifyContent: "center" }}
          >
            <IconButton onClick={handlePrint}>
              <LocalPrintshopIcon
                title="Imprimir"
                sx={{
                  "&:hover": {
                    cursor: "pointer",
                    color: grey[400],
                  },
                }}
              />
            </IconButton>
          </Box>
        </>
      )}
    </Paper>
  );
}
